import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useMediaQuery, useTheme } from '@mui/material';

import { Drawer, Stack, tokens } from '@cast/design-system';
import type { ChatMessage } from '@cast/types';

import { useOrganizations } from '@cast/shared-organization-context-data-access';

import { trackOpsPilot } from './_analytics';
import { ChatErrorBoundary } from './ChatErrorBoundary';
import { ChatFooter } from './ChatFooter';
import { ChatHeader } from './ChatHeader';
import { EmptyState } from './EmptyState';
import { HistorySidebar } from './HistorySidebar';
import { MessagesList } from './MessagesList';
import { useA2AChat } from './useA2AChat';
import { useChatbotState } from './useChatbotState';
import { useChatHistory } from './useChatHistory';
import { useChatWithTracking } from './useChatWithTracking';
import { useFocusTextarea } from './useFocusTextarea';
import { useNewChatAnimation } from './useNewChatAnimation';
import { useScrollToBottom } from './useScrollToBottom';
import { useShowHistory } from './useShowHistory';

export const AIAssistantSidebar = () => {
  const {
    isOpen,
    initialMessage,
    clearInitialMessage,
    incrementUnread,
    isFullScreen,
    toggleFullScreen,
    inputValue,
    setInputValue,
    hadUnreadOnOpen,
    clearHadUnreadOnOpen,
  } = useChatbotState();
  const { currentOrganization } = useOrganizations();
  const [currentChatId, setCurrentChatId] = useState<string | undefined>();
  const currentChatIdRef = useRef(currentChatId);
  currentChatIdRef.current = currentChatId;
  const pendingNewChatIdRef = useRef<string | undefined>(undefined);

  const { breakpoints } = useTheme();
  const smallestBreakpoint = breakpoints.values.laptop;
  const isCompact = useMediaQuery(breakpoints.down('laptop'));

  const { history, saveChat, deleteChat, getChat } = useChatHistory();
  const { showHistory, setShowHistory, handleCloseChatbot } = useShowHistory();
  const { dummyRef, showScrollToBottom, scrollToBottom, handleScroll } =
    useScrollToBottom();

  const { textareaRef, focusTextarea } = useFocusTextarea();

  const handleAgentResponse = useCallback(
    (
      updatedMessages: ChatMessage[],
      contextId: string,
      snapshotChatId: string | undefined
    ) => {
      // Save chat when agent responds, but only if it has agent messages
      if (updatedMessages.some((m) => m.role === 'agent')) {
        const chatId = saveChat(
          updatedMessages,
          snapshotChatId ?? pendingNewChatIdRef.current,
          contextId
        );
        // Only assign a new chat ID if this was a new chat and we haven't navigated away
        if (chatId && !snapshotChatId && !currentChatIdRef.current) {
          setCurrentChatId(chatId);
          currentChatIdRef.current = chatId;
        }
        if (!snapshotChatId) {
          pendingNewChatIdRef.current = undefined;
        }
        incrementUnread();
      }
    },
    [saveChat, incrementUnread]
  );

  const handleUserMessageSent = useCallback(
    (
      msgs: ChatMessage[],
      ctxId: string,
      snapshotChatId: string | undefined
    ) => {
      const id = saveChat(msgs, snapshotChatId, ctxId);
      if (id && !snapshotChatId) {
        pendingNewChatIdRef.current = id;
      }
    },
    [saveChat]
  );

  const {
    sendMessage,
    messages,
    loading,
    loadingChatId,
    thinkingText,
    error,
    resetChat,
    setMessages,
    setContextId,
    getContextId,
    pendingRetry,
    retrySend,
    clearPendingRetry,
  } = useA2AChat(
    handleAgentResponse,
    () => currentChatIdRef.current,
    handleUserMessageSent
  );

  // Only show thinking bubble when the in-flight request belongs to the current chat
  const loadingResponse = loading && loadingChatId === currentChatId;

  const hasConversation = messages.length > 0;

  const {
    handleInputChange,
    handleSendMessage,
    handleSuggestionClick,
    closeViaButton,
    countQuestion,
  } = useChatWithTracking({
    getContextId,
    handleCloseChatbot,
    sendMessage,
    focusTextarea,
    currentChatIdRef,
  });

  const startNewChat = useCallback(() => {
    // Chat is already saved when agent responds, no need to save again
    currentChatIdRef.current = undefined;
    pendingNewChatIdRef.current = undefined;
    resetChat();
    setCurrentChatId(undefined);
    setInputValue('');
    focusTextarea();
    if (isCompact) {
      setShowHistory(false);
    }
  }, [resetChat, isCompact, setInputValue, focusTextarea, setShowHistory]);

  const {
    historyBtnRef,
    messagesContainerRef,
    containerStyle,
    triggerAnimation,
  } = useNewChatAnimation({
    enabled: hasConversation && !showHistory,
    onAnimationEnd: startNewChat,
  });

  const handleStartNewChat = useCallback(() => {
    trackOpsPilot.newChatStarted({
      context_id: getContextId(),
      organization_id: currentOrganization?.id,
    });
    triggerAnimation();
  }, [triggerAnimation, getContextId, currentOrganization?.id]);

  const handleToggleFullScreen = useCallback(() => {
    // isFullScreen is the state *before* the toggle, so the new value is its inverse.
    trackOpsPilot.fullScreenToggled({
      context_id: getContextId(),
      organization_id: currentOrganization?.id,
      is_full_screen: !isFullScreen,
    });
    toggleFullScreen();
  }, [toggleFullScreen, isFullScreen, getContextId, currentOrganization?.id]);

  const handleToggleHistory = useCallback(() => {
    if (!showHistory) {
      trackOpsPilot.historyOpened({
        context_id: getContextId(),
        organization_id: currentOrganization?.id,
      });
    }
    setShowHistory((prev) => !prev);
  }, [showHistory, setShowHistory, getContextId, currentOrganization?.id]);

  const handleSelectChat = useCallback(
    (chatId: string) => {
      // Chat is already saved when agent responds, no need to save again when switching
      const chat = getChat(chatId);
      if (!chat) return;
      if (isCompact) {
        setShowHistory(false);
      }
      clearPendingRetry();
      currentChatIdRef.current = chatId;
      setMessages(chat.messages);
      setCurrentChatId(chatId);

      // Restore contextId if the chat has one
      if (chat.contextId) {
        setContextId(chat.contextId);
      }

      focusTextarea();
    },
    [
      getChat,
      isCompact,
      setMessages,
      setContextId,
      focusTextarea,
      setShowHistory,
      clearPendingRetry,
    ]
  );

  const handleDeleteChat = useCallback(
    (chatId: string) => {
      deleteChat(chatId);
      // If we're deleting the current chat, start a new one
      if (chatId === currentChatId) {
        currentChatIdRef.current = undefined;
        resetChat();
        setCurrentChatId(undefined);
        setInputValue('');
      }
    },
    [deleteChat, currentChatId, resetChat, setInputValue]
  );

  // Reset chat when organization changes
  useEffect(() => {
    currentChatIdRef.current = undefined;
    resetChat();
    setCurrentChatId(undefined);
    setInputValue('');
  }, [currentOrganization?.id, resetChat, setInputValue]);

  // Navigate to the most recent chat when opening with unread messages.
  useEffect(() => {
    if (!isOpen || !hadUnreadOnOpen) return;
    clearHadUnreadOnOpen();
    const mostRecent = history[0];
    if (mostRecent && mostRecent.id !== currentChatIdRef.current) {
      const chat = getChat(mostRecent.id);
      if (!chat) return;
      clearPendingRetry();
      currentChatIdRef.current = mostRecent.id;
      setMessages(chat.messages);
      setCurrentChatId(mostRecent.id);
      if (chat.contextId) setContextId(chat.contextId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, hadUnreadOnOpen]);

  useEffect(() => {
    // Don't focus input on mobile — virtual keyboard pushes nav out of viewport.
    if (!isOpen || isCompact) return;
    focusTextarea();
    scrollToBottom('instant');
  }, [isOpen, isCompact, focusTextarea, scrollToBottom]);

  useEffect(() => {
    if (!isOpen) return;
    scrollToBottom();
  }, [isOpen, messages, scrollToBottom]);

  // Handle initial message when chatbot opens with a predefined query
  useEffect(() => {
    if (isOpen && initialMessage) {
      resetChat();
      setCurrentChatId(undefined);
      currentChatIdRef.current = undefined;
      countQuestion(initialMessage);
      sendMessage(initialMessage, undefined, 'predefined');
      clearInitialMessage();
    }
  }, [
    isOpen,
    initialMessage,
    sendMessage,
    clearInitialMessage,
    resetChat,
    setCurrentChatId,
    countQuestion,
  ]);

  useEffect(() => {
    if (!isOpen || !isCompact) return;
    const getViewport = () =>
      document.querySelector<HTMLMetaElement>('meta[name="viewport"]');
    const originalFallbackWidth = document.documentElement.clientWidth;

    getViewport()?.setAttribute('content', 'width=device-width');

    return () => {
      getViewport()?.setAttribute(
        'content',
        `width=${originalFallbackWidth || smallestBreakpoint}`
      );
    };
  }, [isOpen, isCompact, smallestBreakpoint]);

  const conversationContext = useMemo(
    () =>
      currentOrganization
        ? {
            organizationId: currentOrganization.id,
            chatId: currentChatId,
            getChat,
            getLiveContextId: getContextId,
          }
        : undefined,
    [currentOrganization, currentChatId, getChat, getContextId]
  );

  const getDrawerWidth = () => {
    if (isFullScreen || isCompact) return '100dvw';
    if (showHistory) return 760;
    return 480;
  };

  const onFollowUpClick = useCallback(
    (command: string) => {
      const snapshotChatId = currentChatIdRef.current;
      sendMessage(command, snapshotChatId).then(() => {
        focusTextarea();
      });
    },
    [focusTextarea, sendMessage]
  );

  return (
    <Drawer open={isOpen} size="small" onClose={handleCloseChatbot}>
      <Stack
        component="aside"
        direction="row"
        sx={{
          position: 'fixed',
          right: 0,
          top: 0,
          backgroundColor: tokens.theme.bg.structural.surface,
          width: getDrawerWidth(),
          height: '100dvh',
          transition: isCompact
            ? 'none'
            : 'transform 0.25s, width 0.3s ease-in-out',
          overflow: 'hidden',
        }}
      >
        <ChatErrorBoundary onClose={closeViaButton} onNewChat={startNewChat}>
          <HistorySidebar
            isMobile={isCompact}
            showHistory={isFullScreen || showHistory}
            history={history}
            currentChatId={currentChatId}
            onSelectChat={handleSelectChat}
            onDeleteChat={handleDeleteChat}
            onNewChat={startNewChat}
          />

          <Stack direction="column" sx={{ flex: 1, minWidth: 0 }}>
            <ChatHeader
              onToggleHistory={handleToggleHistory}
              onStartNewChat={handleStartNewChat}
              onClose={closeViaButton}
              showHistory={showHistory}
              showFullScreenButton={!isCompact}
              isFullScreen={isFullScreen}
              onToggleFullScreen={handleToggleFullScreen}
              historyBtnRef={historyBtnRef}
            />
            {hasConversation ? (
              <Stack
                ref={messagesContainerRef}
                direction="column"
                sx={{
                  flex: 1,
                  minHeight: 0,
                  ...containerStyle,
                }}
              >
                <MessagesList
                  messages={messages}
                  loadingResponse={loadingResponse}
                  error={error}
                  onScroll={handleScroll}
                  dummyRef={dummyRef}
                  onScrollToBottom={scrollToBottom}
                  conversationContext={conversationContext}
                  pendingRetry={pendingRetry}
                  onRetry={retrySend}
                  onFollowUpClick={onFollowUpClick}
                  thinkingText={thinkingText}
                />
              </Stack>
            ) : (
              <EmptyState onSuggestionClick={handleSuggestionClick} />
            )}
            <ChatFooter
              inputValue={inputValue}
              onInputChange={handleInputChange}
              onSendMessage={handleSendMessage}
              loadingResponse={loadingResponse}
              showScrollToBottom={showScrollToBottom}
              onScrollToBottom={scrollToBottom}
              textareaRef={textareaRef}
            />
          </Stack>
        </ChatErrorBoundary>
      </Stack>
    </Drawer>
  );
};
